import React from 'react';
import PhoneNumberInput from './PhoneNumberInput';

interface OrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  title: string;
  form: any;
  setForm: (form: any) => void;
  isEdit?: boolean;
  saving: boolean;
  formError: string;
  typesLoading: boolean;
  displayTypes: string[];
  subscriptionStatusOptions: string[];
  subscriptionCycleOptions: string[];
  addTypeToForm: (type: string, isEdit?: boolean) => void;
  removeTypeFromForm: (type: string, isEdit?: boolean) => void;
}

const OrganizationModal: React.FC<OrganizationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  form,
  setForm,
  isEdit = false,
  saving,
  formError,
  typesLoading,
  displayTypes,
  subscriptionStatusOptions,
  subscriptionCycleOptions,
  addTypeToForm,
  removeTypeFromForm
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-8 border w-full max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
          <form onSubmit={onSubmit}>
            <div className="grid grid-cols-2 gap-2">
              {/* Left Column - Organization Details */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 mb-4">Organization</h4>
                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">Legal Entity Name *</label>
                  <div className="col-span-3">
                    <input
                      type="text"
                      value={form.legal_entity_name}
                      onChange={(e) => setForm({ ...form, legal_entity_name: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">Website</label>
                  <div className="col-span-3">
                    <input
                      type="url"
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 items-start">
                  <label className="text-sm font-medium text-gray-700 text-right pt-2">Types</label>
                  <div className="col-span-3">
                    <div className="border border-gray-300 rounded-md bg-white">
                      <div className="p-2 min-h-[100px] max-h-32 overflow-y-auto">
                        {typesLoading ? (
                          <div className="flex items-center justify-center h-16">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-sm text-gray-500">Loading types...</span>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-1">
                            {displayTypes.map((type) => (
                              <label key={type} className="flex items-center p-1 hover:bg-gray-50 rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={form.types?.includes(type) || false}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      addTypeToForm(type, isEdit);
                                    } else {
                                      removeTypeFromForm(type, isEdit);
                                    }
                                  }}
                                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">{type}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Select multiple types by checking the boxes
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">Subscription Status *</label>
                  <div className="col-span-3 relative">
                    <select
                      value={form.subscription_status}
                      onChange={(e) => setForm({ ...form, subscription_status: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                      required
                    >
                      {subscriptionStatusOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">Subscription Cycle</label>
                  <div className="col-span-3 relative">
                    <select
                      value={form.subscription_cycle || ''}
                      onChange={(e) => setForm({ ...form, subscription_cycle: e.target.value || null })}
                      className="w-full border border-gray-300 rounded-md px-3 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                    >
                      <option value="">Select cycle</option>
                      {subscriptionCycleOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">Subscription Start Date</label>
                  <div className="col-span-3">
                    <input
                      type="date"
                      value={form.subscription_start_date || ''}
                      onChange={(e) => setForm({ ...form, subscription_start_date: e.target.value || null })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column - Primary Contact User */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 mb-4">Primary Contact User</h4>
                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">First Name *</label>
                  <div className="col-span-3">
                    <input
                      type="text"
                      value={form.primary_contact?.first_name || ''}
                      onChange={(e) => setForm({
                        ...form,
                        primary_contact: {
                          ...form.primary_contact!,
                          first_name: e.target.value
                        }
                      })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">Last Name *</label>
                  <div className="col-span-3">
                    <input
                      type="text"
                      value={form.primary_contact?.last_name || ''}
                      onChange={(e) => setForm({
                        ...form,
                        primary_contact: {
                          ...form.primary_contact!,
                          last_name: e.target.value
                        }
                      })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">Email Address *</label>
                  <div className="col-span-3">
                    <input
                      type="email"
                      value={form.primary_contact?.email_address || ''}
                      onChange={(e) => setForm({
                        ...form,
                        primary_contact: {
                          ...form.primary_contact!,
                          email_address: e.target.value
                        }
                      })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">Phone Number</label>
                  <div className="col-span-3">
                    <PhoneNumberInput
                      countryCode={form.primary_contact?.country_code || '+65'}
                      phoneNumber={form.primary_contact?.phone_number || ''}
                      onCountryCodeChange={(countryCode) => setForm({
                        ...form,
                        primary_contact: {
                          ...form.primary_contact!,
                          country_code: countryCode
                        }
                      })}
                      onPhoneNumberChange={(phoneNumber) => setForm({
                        ...form,
                        primary_contact: {
                          ...form.primary_contact!,
                          phone_number: phoneNumber
                        }
                      })}
                      placeholder="Phone number"
                      className="border-0"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">Job Title</label>
                  <div className="col-span-3">
                    <input
                      type="text"
                      value={form.primary_contact?.job_title || ''}
                      onChange={(e) => setForm({
                        ...form,
                        primary_contact: {
                          ...form.primary_contact!,
                          job_title: e.target.value
                        }
                      })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 items-center">
                  <label className="text-sm font-medium text-gray-700 text-right">Department</label>
                  <div className="col-span-3">
                    <input
                      type="text"
                      value={form.primary_contact?.department || ''}
                      onChange={(e) => setForm({
                        ...form,
                        primary_contact: {
                          ...form.primary_contact!,
                          department: e.target.value
                        }
                      })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
            {formError && (
              <div className="mt-4 text-red-600 text-sm">{formError}</div>
            )}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrganizationModal;
